import GeoJSON from 'ol/format/GeoJSON'
import VectorLayer from 'ol/layer/Vector'
import WebGLVectorLayer from 'ol/layer/WebGLVector'
import VectorSource from 'ol/source/Vector'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import { shallowRef } from 'vue'
import {
  TRANSACTION_MAP_FULL_LOAD_ZOOM,
  TRANSACTION_MAP_PARCEL_LAYER,
  TRANSACTION_MAP_PARCEL_ZOOM,
  TRANSACTION_MAP_PARCEL_ZOOM_BANDS,
} from '@/constants'
import { isWebGLSupported } from '@/services/map/webglSupport'
import { useTransactionStore } from '@/stores/transaction'

// WebGL flat styles — the whole layer renders on the GPU (older-device
// friendly at country scale). Filters must be exhaustive and only reference
// attributes stamped on every feature (`multi` is always 0/1).
// Multi-parcel deals (one transaction, several katastritunnused) read as one
// group: same blue, dashed + heavier stroke.
const PARCEL_WEBGL_STYLES = [
  {
    filter: ['==', ['get', 'multi'], 1],
    style: {
      'stroke-color': '#027BFA',
      'stroke-width': 2.5,
      'stroke-line-dash': [6, 4],
      'fill-color': 'rgba(2, 123, 250, 0.09)',
    },
  },
  {
    filter: ['!=', ['get', 'multi'], 1],
    style: {
      'stroke-color': '#027BFA',
      'stroke-width': 1.5,
      'fill-color': 'rgba(2, 123, 250, 0.06)',
    },
  },
]

// Canvas fallback styles (browser without WebGL, or layer creation failure)
const fallbackBaseStyle = new Style({
  stroke: new Stroke({ color: '#027BFA', width: 1.5 }),
  fill: new Fill({ color: 'rgba(2, 123, 250, 0.06)' }),
})
const fallbackMultiStyle = new Style({
  stroke: new Stroke({ color: '#027BFA', width: 2.5, lineDash: [6, 4] }),
  fill: new Fill({ color: 'rgba(2, 123, 250, 0.09)' }),
})

const PARCEL_BATCH_SIZE = 200 // endpoint validation cap

const bandForZoom = zoom => {
  for (let i = TRANSACTION_MAP_PARCEL_ZOOM_BANDS.length - 1; i >= 0; i--) {
    if (zoom >= TRANSACTION_MAP_PARCEL_ZOOM_BANDS[i].min) {
      return i
    }
  }
  return 0
}

/**
 * Cadastre-boundary layer for transaction markers (RED-255).
 *
 * At zoom >= TRANSACTION_MAP_PARCEL_ZOOM every visible single feature (and,
 * at full-load zoom, small-cluster members) has its parcel polygons fetched
 * (ids-based, batched) and rendered as a subtle blue outline. The server
 * culls parcels smaller than a few pixels at the band's finest resolution,
 * so coarse zooms ship only what is actually visible. Fetched ids are
 * remembered per zoom band; a filter-context change clears everything.
 */
export function useTransactionParcels() {
  const transactionStore = useTransactionStore()
  const parcelSource = new VectorSource()
  const parcelLayer = shallowRef(null)
  const format = new GeoJSON()
  // `${id}@${band}` — per-band memory because the server cull is resolution-dependent
  const requestedIds = new Set()
  // transaction_id -> Feature[] for O(1) sibling lookups on hover
  const parcelsByTransaction = new globalThis.Map()
  let syncSequence = 0

  const createParcelLayer = zIndex => {
    // Probe before constructing: OL creates the WebGL context lazily on the
    // first render frame, so on a WebGL-less browser the constructor
    // succeeds and the failure explodes later in the map's render loop.
    let layer = null
    if (isWebGLSupported()) {
      try {
        layer = new WebGLVectorLayer({
          source: parcelSource,
          style: PARCEL_WEBGL_STYLES,
          minZoom: TRANSACTION_MAP_PARCEL_ZOOM,
        })
      } catch (error) {
        console.warn('WebGL parcel layer failed, falling back to canvas:', error)
      }
    }
    if (!layer) {
      layer = new VectorLayer({
        source: parcelSource,
        style: feature =>
          feature.get('multi') === 1 ? fallbackMultiStyle : fallbackBaseStyle,
        minZoom: TRANSACTION_MAP_PARCEL_ZOOM,
        updateWhileAnimating: true,
        updateWhileInteracting: true,
      })
    }
    layer.setZIndex(zIndex)
    layer.set('id', TRANSACTION_MAP_PARCEL_LAYER)
    parcelLayer.value = layer
    return layer
  }

  const getParcelsByTransactionId = id =>
    parcelsByTransaction.get(String(id)) || []

  // Geometric containment lookup (R-tree backed) — WebGL pixel hit-detection
  // effectively ignores the near-transparent fill, so hover/click resolve
  // parcels against the source geometry instead.
  const getParcelsAtCoordinate = coordinate =>
    parcelSource.getFeaturesAtCoordinate(coordinate)

  /**
   * Fetch + render boundaries for the not-yet-requested features in view.
   * Extent-filtered: below the full-load zoom the source holds the whole
   * country — only what the user can see is requested. Cluster members join
   * at full-load zoom, where cells are tiny; that keeps an outline alive when
   * a filter change collapses its single marker into a small cluster.
   * @param {Array} features - currently loaded transaction map features
   * @param {number} zoom - current map zoom (integer)
   * @param {Array|null} extent - visible extent [minX, minY, maxX, maxY]
   * @param {import('ol/View').default|null} view - map view for cull resolution
   */
  const syncParcels = async (features, zoom, extent = null, view = null) => {
    if (zoom == null || zoom < TRANSACTION_MAP_PARCEL_ZOOM) {
      return
    }

    const band = bandForZoom(zoom)
    const cullResolution = view
      ? view.getResolutionForZoom(TRANSACTION_MAP_PARCEL_ZOOM_BANDS[band].cullZoom)
      : null
    const includeClusterMembers = zoom >= TRANSACTION_MAP_FULL_LOAD_ZOOM

    const ids = []
    const collect = id => {
      const key = `${id}@${band}`
      if (!requestedIds.has(key)) {
        requestedIds.add(key)
        ids.push(id)
      }
    }
    features.forEach(feature => {
      if (extent && !feature.getGeometry()?.intersectsExtent(extent)) {
        return
      }
      if (feature.get('cluster')) {
        if (includeClusterMembers) {
          ;(feature.get('point_ids') || []).forEach(collect)
        }
        return
      }
      const id = feature.get('id')
      if (id != null) {
        collect(id)
      }
    })
    if (ids.length === 0) {
      return
    }

    const sequence = syncSequence
    const rollback = batchIds =>
      batchIds.forEach(id => requestedIds.delete(`${id}@${band}`))

    for (let i = 0; i < ids.length; i += PARCEL_BATCH_SIZE) {
      const batchIds = ids.slice(i, i + PARCEL_BATCH_SIZE)
      try {
        const { parcels, skipped_transaction_ids: skippedIds } =
          await transactionStore.loadParcelBoundaries(batchIds, cullResolution)
        // A clear() happened while this batch was in flight — drop it and
        // un-mark the whole remainder so the next sync retries it.
        if (sequence !== syncSequence) {
          rollback(ids.slice(i))
          return
        }
        // Ids the server truncated away (MAX_TUNNUSED cap) must retry too
        if (skippedIds.length > 0) {
          rollback(skippedIds)
        }

        // One feature per parcel (the server dedupes by tunnus): apartment
        // buildings put dozens of transactions on one plot — stacking a copy
        // per transaction rendered as an opaque blob and made hover/click
        // resolve to an arbitrary one of them.
        const parcelFeatures = []
        parcels.forEach(parcel => {
          if (!parcel.geometry) {
            return
          }
          const owningIds = parcel.transaction_ids || []
          let feature = parcelSource.getFeatureById(parcel.tunnus)
          if (!feature) {
            feature = format.readFeature({
              type: 'Feature',
              geometry: parcel.geometry,
              properties: {
                tunnus: parcel.tunnus,
                transaction_ids: owningIds,
                multi: 0,
              },
            })
            feature.setId(parcel.tunnus)
            parcelFeatures.push(feature)
          } else {
            // Another batch delivered more owners of an already-drawn parcel
            const merged = new Set([...(feature.get('transaction_ids') || []), ...owningIds])
            feature.set('transaction_ids', [...merged], true)
          }
          owningIds.forEach(id => {
            const key = String(id)
            if (!parcelsByTransaction.has(key)) {
              parcelsByTransaction.set(key, [])
            }
            const list = parcelsByTransaction.get(key)
            if (!list.includes(feature)) {
              list.push(feature)
            }
          })
        })
        if (parcelFeatures.length > 0) {
          parcelSource.addFeatures(parcelFeatures)
        }
        // Deals spanning >1 parcels render dashed as one group — stamped after
        // indexing so a deal's parcels arriving in any order all pick it up
        parcels.forEach(parcel => {
          ;(parcel.transaction_ids || []).forEach(id => {
            const deal = parcelsByTransaction.get(String(id)) || []
            if (deal.length > 1) {
              deal.forEach(feature => {
                if (feature.get('multi') !== 1) {
                  feature.set('multi', 1)
                }
              })
            }
          })
        })
      } catch (error) {
        console.error('Error loading parcel boundaries:', error)
        // Never blacklist a failed batch — retry on the next sync
        rollback(ids.slice(i))
        return
      }
    }
  }

  const clearParcels = () => {
    syncSequence++
    parcelSource.clear()
    requestedIds.clear()
    parcelsByTransaction.clear()
  }

  const disposeParcels = () => {
    clearParcels()
    parcelLayer.value?.dispose?.()
    parcelLayer.value = null
  }

  return {
    parcelLayer,
    createParcelLayer,
    syncParcels,
    clearParcels,
    disposeParcels,
    getParcelsByTransactionId,
    getParcelsAtCoordinate,
  }
}
